import { Directive, ElementRef, EventEmitter, HostListener, inject, Input, Output, Renderer2 } from '@angular/core';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[lnHorizontalSwipe]',
})
export class HorizontalSwipeDirective {
  @Output() swipeToLeft = new EventEmitter<void>();
  @Output() swipeToRight = new EventEmitter<void>();

  /** Enable/disable swipes (e.g., disable when zoomed) */
  @Input() swipeEnabled = true;

  /** Pixel distance threshold to qualify as a swipe */
  @Input() minDistance = 40;
  private readonly MIN_DISTANCE = 40;
  /** Min horizontal velocity (px/ms) to count as a flick */
  @Input() flickVelocity = 0.35; // ~350 px/s
  private readonly FLICK_VELOCITY = 0.35;
  private startX = 0;
  private startY = 0;
  private startT = 0;
  private swiping = false;
  private horizontalLocked = false;
  private startedOnHost = false;
  private el = inject(ElementRef<HTMLElement>);
  private r = inject(Renderer2);
  constructor() {
    // Allow vertical scrolling; we’ll capture horizontal swipes
    this.r.setStyle(this.el.nativeElement, 'touch-action', 'pan-y');
    this.r.setStyle(this.el.nativeElement, 'user-select', 'none');
  }

  private activeTouchId: number | null = null;

  @HostListener('touchstart', ['$event'])
  onTouchStart(e: TouchEvent) {
    if (this.swipeEnabled && e.touches.length === 1 && this.el.nativeElement.contains(e.target as Node)) {
      const t = e.touches[0];
      this.startX = t.clientX;
      this.startY = t.clientY;
      this.startT = performance.now();
      this.activeTouchId = t.identifier; // ✅ track the finger
      this.swiping = true;
      this.horizontalLocked = false;
      this.startedOnHost = true;
    }
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(e: TouchEvent) {
    if (!this.swiping || !this.startedOnHost || this.activeTouchId == null) return;

    const t = Array.from(e.touches).find(tt => tt.identifier === this.activeTouchId);
    if (!t) return; // our finger not in the touches anymore

    const dx = t.clientX - this.startX;
    const dy = t.clientY - this.startY;

    if (!this.horizontalLocked) {
      if (Math.abs(dy) > Math.abs(dx)) {
        this.reset(); // vertical scroll → cancel swipe detection
        return;
      } else {
        this.horizontalLocked = true;
        // no preventDefault needed thanks to touch-action: pan-y
      }
    }
  }

  @HostListener('document:touchend', ['$event'])
  onTouchEnd(e: TouchEvent) {
    if (!this.swiping || !this.startedOnHost || this.activeTouchId == null) return;

    const changed = Array.from(e.changedTouches).find(tt => tt.identifier === this.activeTouchId);
    if (!changed) {
      this.reset();
      return;
    }

    const dt = performance.now() - this.startT;
    const dx = changed.clientX - this.startX;
    const v = Math.abs(dx) / Math.max(1, dt);

    const DIST = this.minDistance ?? this.MIN_DISTANCE;
    const FLICK = this.flickVelocity ?? this.FLICK_VELOCITY;

    if (this.horizontalLocked) {
      if (dx <= -DIST || (v > FLICK && dx < 0)) this.swipeToLeft.emit();
      else if (dx >= DIST || (v > FLICK && dx > 0)) this.swipeToRight.emit();
    }
    this.reset();
  }

  @HostListener('document:touchcancel')
  onTouchCancel() {
    this.reset();
  }

  private reset() {
    this.swiping = false;
    this.horizontalLocked = false;
    this.startedOnHost = false;
    this.activeTouchId = null; // ✅ clear tracked finger
    this.startT = 0;
  }
}
